import type { BulkUpdateFeatureResponse, Organization } from '../services/api';

/**
 * Reads the per-organisation outcome of a bulk feature update.
 *
 * The endpoint answers HTTP 200 even when it refused some (or all) of the
 * organisations — the refusals live in `data.results[].success`, and the
 * `success` flag at the top is `failed === 0`. So a caller that only awaits the
 * promise sees a resolved call and reports a win.
 *
 * That mattered little while the backend's stack check ran for 7 of 71
 * features. Since `availableOn` became mandatory (legal-ai-backend!1367) it runs
 * for every one of them, so a refusal is now an ordinary outcome an admin needs
 * to see rather than a near-impossible edge case.
 *
 * Grouping is by reason rather than by organisation: an "enable for all" across
 * ~200 organisations produces one identical refusal per organisation of the
 * wrong stack, and 128 rows saying the same sentence hides the one sentence that
 * matters.
 */

export interface BulkUpdateFailureGroup {
  /** The server's message, verbatim — never re-worded here. */
  reason: string;
  /** Display names where known, falling back to the raw id. */
  organizationNames: string[];
}

export interface BulkUpdateOutcome {
  succeeded: number;
  failed: number;
  /**
   * Empty when nothing was refused — and NON-empty whenever `failed > 0`. The two
   * always agree, so a caller may gate on either; `failureGroups.length` is the
   * safer one, because it is the thing there is actually something to render for.
   */
  failureGroups: BulkUpdateFailureGroup[];
}

const UNPARSEABLE_REASON =
  'The server reported that the update did not fully succeed, but did not say which organisations were affected. Re-check the feature before assuming it is switched on.';

/**
 * @param resolveOrganizationName id -> display name; return the id itself when
 *   the organisation is not in the caller's list.
 */
export function summariseBulkUpdate(
  response: BulkUpdateFeatureResponse | undefined | null,
  resolveOrganizationName: (organizationId: string) => string,
): BulkUpdateOutcome {
  const results = response?.data?.results;

  // No parseable per-org detail. Trust the top-level flag ONLY when it is
  // explicitly true — an absent/undefined response must not read as a pass.
  if (!Array.isArray(results) || results.length === 0) {
    const looksSuccessful = response?.success === true;
    const failed = response?.data?.summary?.failed;
    if (looksSuccessful && (failed === undefined || failed === 0)) {
      return {
        succeeded: response?.data?.summary?.succeeded ?? 0,
        failed: 0,
        failureGroups: [],
      };
    }
    return {
      succeeded: response?.data?.summary?.succeeded ?? 0,
      // At least 1. A contradictory `{ success: false, summary: { failed: 0 } }`
      // would otherwise return "nothing failed" while carrying a failure to report —
      // and any caller gating on `failed > 0` would close on it, which is the exact
      // "closing on I-could-not-tell" this branch exists to prevent.
      failed: Math.max(failed ?? 1, 1),
      failureGroups: [{ reason: UNPARSEABLE_REASON, organizationNames: [] }],
    };
  }

  const byReason = new Map<string, string[]>();
  let succeeded = 0;
  let failed = 0;

  for (const result of results) {
    if (result.success) {
      succeeded++;
      continue;
    }
    failed++;
    const reason = result.error?.trim() || 'No reason given';
    const names = byReason.get(reason) ?? [];
    names.push(resolveOrganizationName(result.organizationId));
    byReason.set(reason, names);
  }

  return {
    succeeded,
    failed,
    // Biggest group first — with a mixed run, the systematic refusal is the
    // one an admin needs to read, not whichever organisation sorted first.
    failureGroups: Array.from(byReason.entries())
      .map(([reason, organizationNames]) => ({ reason, organizationNames }))
      .sort((a, b) => b.organizationNames.length - a.organizationNames.length),
  };
}

/**
 * Builds the id -> display-name lookup `summariseBulkUpdate` needs.
 *
 * Take the list from the response you have just fetched, NOT from component
 * state: the caller refreshes organisations immediately before summarising, and
 * React has not applied that `setState` yet, so the state copy is one request
 * out of date. An organisation created since the page loaded would resolve to a
 * raw id for no reason.
 */
export function makeOrganizationNameResolver(
  organizations: readonly Organization[],
): (organizationId: string) => string {
  const byId = new Map(organizations.map(org => [org.id, org.displayName || org.name]));
  return organizationId => byId.get(organizationId) || organizationId;
}
